import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
/**
 * ManpowerRequestModal.js
 * Detailed view modal for reviewing a manpower request
 * Allows HR to approve, reject, or create a job post from the request
 */
import React, { useState } from 'react';
import './ManpowerRequest.css';

const ManpowerRequestModal = ({
    request,
    onClose,
    onApprove,
    onReject,
    onCreateJob,
    onMarkFulfilled, // New prop for manual fulfillment
    canManageRequests = true
}) => {
  useUiLanguage();
    const [showRejectReason, setShowRejectReason] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [showFulfillmentNotes, setShowFulfillmentNotes] = useState(false);
    const [fulfillmentNotes, setFulfillmentNotes] = useState('');

    if (!request) return null;

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleDateString(getUiLocale(), {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get readable labels
    const getEmploymentTypeLabel = (type) => {
        switch (type) {
            case 'full-time': return uiText('Full-Time');
            case 'part-time': return uiText('Part-Time');
            case 'contract': return uiText('Contract');
            default: return type || 'N/A';
        }
    };

    const getUrgencyLabel = (urgency) => {
        switch (urgency) {
            case 'immediate': return uiText('Immediate');
            case '30_days': return uiText('Within 30 Days');
            case '90_days': return uiText('Within 90 Days');
            case 'not_urgent': return uiText('Not Urgent');
            default: return urgency || 'N/A';
        }
    };

    const getBudgetStatusLabel = (status) => {
        switch (status) {
            case 'budgeted': return uiText('Budgeted');
            case 'unbudgeted': return uiText('Unbudgeted');
            case 'pending': return uiText('Pending Finance Approval');
            default: return status || 'N/A';
        }
    };

    const getNationalityLabel = (pref) => {
        switch (pref) {
            case 'local': return uiText('Local Candidates Preferred');
            case 'any': return uiText('Open to All Nationalities');
            case 'specific': return uiText('Specific Nationality Required');
            // Legacy values for existing data
            case 'qatari': return uiText('Local National');
            case 'gcc': return uiText('Regional National');
            default: return pref || 'No Preference';
        }
    };

    const getVisaLabel = (visa) => {
        switch (visa) {
            case 'local': return uiText('Local Hire Only (No Sponsorship)');
            case 'transfer': return uiText('Work Permit Transfer Acceptable');
            case 'new': return uiText('New Work Permit / Sponsorship Required');
            default: return visa || 'Any';
        }
    };

    const getExperienceLabel = (exp) => {
        switch (exp) {
            case 'entry': return uiText('Entry Level (0-2 years)');
            case 'mid': return uiText('Mid Level (3-5 years)');
            case 'senior': return uiText('Senior (6-10 years)');
            case 'expert': return uiText('Expert (10+ years)');
            default: return exp || 'N/A';
        }
    };

    // Handle approve
    const handleApprove = async () => {
        if (processing) return;
        setProcessing(true);
        try {
            await onApprove();
        } finally {
            setProcessing(false);
        }
    };

    // Handle reject
    const handleReject = async () => {
        if (processing) return;
        if (!showRejectReason) {
            setShowRejectReason(true);
            return;
        }
        setProcessing(true);
        try {
            await onReject(rejectReason);
        } finally {
            setProcessing(false);
        }
    };

    // Handle manual fulfillment
    const handleMarkFulfilled = async () => {
        if (processing) return;
        if (!showFulfillmentNotes) {
            setShowFulfillmentNotes(true);
            return;
        }
        setProcessing(true);
        try {
            await onMarkFulfilled(fulfillmentNotes);
        } finally {
            setProcessing(false);
        }
    };

    // Can take action?
    const canTakeAction = canManageRequests && (request.status === 'pending_hr' || request.status === 'pending_internal');
    const isApproved = request.status === 'approved';
    const isFulfilled = request.status === 'fulfilled';
    const canCreateLinkedJob = canManageRequests && (isApproved || isFulfilled) && !request.linkedJobPostId;
    const canMarkFulfilled = canManageRequests && isApproved && !request.linkedJobPostId; // Approved but no job created yet

    return (
        <div className="manpower-modal-overlay" onClick={onClose}>
            <div className="manpower-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="manpower-modal-header">
                    <h2>{request.position?.title || uiText("Manpower Request")}</h2>
                    <button className="manpower-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="manpower-modal-body">
                    {/* Submitter Info */}
                    <div className="manpower-detail-section">
                        <h4>{uiText("Submitted By")}</h4>
                        <div className="manpower-detail-grid">
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Name")}</span>
                                <span className="manpower-detail-value">{request.submitter?.name || 'N/A'}</span>
                            </div>
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Email")}</span>
                                <span className="manpower-detail-value">{request.submitter?.email || 'N/A'}</span>
                            </div>
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Department")}</span>
                                <span className="manpower-detail-value">{request.submitter?.department || 'N/A'}</span>
                            </div>
                            {request.submitter?.location && (
                                <div className="manpower-detail-item">
                                    <span className="manpower-detail-label">{uiText("Location")}</span>
                                    <span className="manpower-detail-value">{request.submitter.location}</span>
                                </div>
                            )}
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Submitted On")}</span>
                                <span className="manpower-detail-value">{formatDate(request.submittedAt)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Request Type */}
                    <div className="manpower-detail-section">
                        <h4>{uiText("Request Type")}</h4>
                        <div className="manpower-detail-grid">
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Type")}</span>
                                <span className="manpower-detail-value highlight">
                                    {request.requestType === 'replacement' ? uiText("Replacement") : uiText("New Position")}
                                </span>
                            </div>
                            {request.requestType === 'replacement' && request.replacementDetails?.employeeName && (
                                <div className="manpower-detail-item">
                                    <span className="manpower-detail-label">{uiText("Replacing")}</span>
                                    <span className="manpower-detail-value">{request.replacementDetails.employeeName}</span>
                                </div>
                            )}
                            {request.requestType === 'new' && request.budgetStatus && (
                                <div className="manpower-detail-item">
                                    <span className="manpower-detail-label">{uiText("Budget Status")}</span>
                                    <span className="manpower-detail-value">{getBudgetStatusLabel(request.budgetStatus)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Position Details */}
                    <div className="manpower-detail-section">
                        <h4>{uiText("Position Details")}</h4>
                        <div className="manpower-detail-grid">
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Job Title")}</span>
                                <span className="manpower-detail-value highlight">{request.position?.title || 'N/A'}</span>
                            </div>
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Headcount")}</span>
                                <span className="manpower-detail-value">{request.position?.headcount || 1}</span>
                            </div>
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Employment Type")}</span>
                                <span className="manpower-detail-value">{getEmploymentTypeLabel(request.position?.type)}</span>
                            </div>
                            <div className="manpower-detail-item">
                                <span className="manpower-detail-label">{uiText("Urgency")}</span>
                                <span className="manpower-detail-value">{uiText(getUrgencyLabel(request.position?.urgency))}</span>
                            </div>
                            {request.position?.gradeCode && (
                                <div className="manpower-detail-item">
                                    <span className="manpower-detail-label">{uiText("Grade/Band")}</span>
                                    <span className="manpower-detail-value">{request.position.gradeCode}</span>
                                </div>
                            )}
                            {request.position?.salaryRange && (
                                <div className="manpower-detail-item">
                                    <span className="manpower-detail-label">{uiText("Salary Range")}</span>
                                    <span className="manpower-detail-value">{request.position.salaryRange}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* GCC Compliance */}
                    {(request.gccCompliance?.nationalityPref || request.gccCompliance?.visaRequirement) && (
                        <div className="manpower-detail-section">
                            <h4>{uiText("Visa & Nationality")}</h4>
                            <div className="manpower-detail-grid">
                                {request.gccCompliance?.nationalityPref && (
                                    <div className="manpower-detail-item">
                                        <span className="manpower-detail-label">{uiText("Nationality Preference")}</span>
                                        <span className="manpower-detail-value">{getNationalityLabel(request.gccCompliance.nationalityPref)}</span>
                                    </div>
                                )}
                                {request.gccCompliance?.visaRequirement && (
                                    <div className="manpower-detail-item">
                                        <span className="manpower-detail-label">{uiText("Visa Requirement")}</span>
                                        <span className="manpower-detail-value">{getVisaLabel(request.gccCompliance.visaRequirement)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Justification */}
                    <div className="manpower-detail-section">
                        <h4>{uiText("Business Justification")}</h4>
                        <div className="manpower-justification-box">
                            {request.justification || uiText("No justification provided.")}
                        </div>
                    </div>

                    {/* Cost Center */}
                    {request.costCenter && (
                        <div className="manpower-detail-section">
                            <h4>{uiText("Finance")}</h4>
                            <div className="manpower-detail-grid">
                                <div className="manpower-detail-item">
                                    <span className="manpower-detail-label">{uiText("Cost Center")}</span>
                                    <span className="manpower-detail-value">{request.costCenter}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Optional Details */}
                    {(request.optional?.skills || request.optional?.experience || request.optional?.reportingTo) && (
                        <div className="manpower-detail-section">
                            <h4>{uiText("Additional Details")}</h4>
                            <div className="manpower-detail-grid">
                                {request.optional?.skills && (
                                    <div className="manpower-detail-item full-width">
                                        <span className="manpower-detail-label">{uiText("Required Skills")}</span>
                                        <span className="manpower-detail-value">{request.optional.skills}</span>
                                    </div>
                                )}
                                {request.optional?.experience && (
                                    <div className="manpower-detail-item">
                                        <span className="manpower-detail-label">{uiText("Experience Level")}</span>
                                        <span className="manpower-detail-value">{getExperienceLabel(request.optional.experience)}</span>
                                    </div>
                                )}
                                {request.optional?.reportingTo && (
                                    <div className="manpower-detail-item">
                                        <span className="manpower-detail-label">{uiText("Reports To")}</span>
                                        <span className="manpower-detail-value">{request.optional.reportingTo}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Rejection Reason Input */}
                    {showRejectReason && (
                        <div className="manpower-detail-section">
                            <h4>{uiText("Rejection Reason")}</h4>
                            <textarea
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '2px solid #E0E0E0',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontSize: '14px',
                                    minHeight: '80px',
                                    resize: 'vertical'
                                }}
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder={uiText("Enter reason for rejection...")}
                            />
                        </div>
                    )}

                    {/* Previous Rejection Reason */}
                    {request.status === 'rejected' && request.rejectionReason && (
                        <div className="manpower-detail-section">
                            <h4>{uiText("Rejection Reason")}</h4>
                            <div className="manpower-justification-box" style={{ backgroundColor: 'rgba(237, 86, 86, 0.1)' }}>
                                {request.rejectionReason}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="manpower-modal-footer">
                    <div className="manpower-modal-footer-left">
                        {canTakeAction && (
                            <button
                                className="manpower-btn manpower-btn-danger"
                                onClick={handleReject}
                                disabled={processing}
                            >
                                <i className="fa-solid fa-times" />
                                {showRejectReason ? uiText("Confirm Reject") : uiText("Reject")}
                            </button>
                        )}
                    </div>
                    <div className="manpower-modal-footer-right">
                        {canCreateLinkedJob && (
                            <button
                                className="manpower-btn manpower-btn-primary"
                                onClick={onCreateJob}
                            >
                                <i className="fa-solid fa-plus" />{uiText("Create Job Post")}</button>
                        )}
                        {/* Manual fulfillment for approved requests */}
                        {canMarkFulfilled && onMarkFulfilled && (
                            <>
                                {showFulfillmentNotes && (
                                    <input
                                        type="text"
                                        value={fulfillmentNotes}
                                        onChange={(e) => setFulfillmentNotes(e.target.value)}
                                        placeholder={uiText("How was this filled? (optional)")}
                                        className="manpower-reject-input"
                                        style={{ marginRight: '10px' }}
                                        autoFocus
                                    />
                                )}
                                <button
                                    className="manpower-btn"
                                    style={{ '--light-control-fill': '#2ecc71', backgroundColor: '#2ecc71', color: '#fff' }}
                                    onClick={handleMarkFulfilled}
                                    disabled={processing}
                                  data-light-control={"filled"}>
                                    <i className="fa-solid fa-clipboard-check" />
                                    {showFulfillmentNotes ? uiText("Confirm Fulfilled") : uiText("Mark as Fulfilled")}
                                </button>
                            </>
                        )}
                        {canTakeAction && (
                            <button
                                className="manpower-btn manpower-btn-success"
                                onClick={handleApprove}
                                disabled={processing}
                            >
                                <i className="fa-solid fa-check" />{uiText("Approve")}</button>
                        )}
                        {!canTakeAction && (
                            <button
                                className="manpower-btn manpower-btn-secondary"
                                onClick={onClose}
                            >{uiText("Close")}</button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManpowerRequestModal;
